import L from 'leaflet'

/* global ymaps: true */
/* eslint-disable no-underscore-dangle */
// Leaflet's own layer API is built on underscore-prefixed internals
// (map._addZoomLimit, this._map, etc.) — this file has to match it.
// Adapted from https://github.com/shramov/leaflet-plugins/blob/master/layer/tile/Yandex.js
// to bind L explicitly instead of relying on a global, so it works under webpack.

L.Yandex = L.Layer.extend({
  options: {
    type: 'yandex#map',
    mapOptions: {
      balloonAutoPan: false,
      suppressMapOpenBlock: true,
    },
    overlayOpacity: 0.8,
    minZoom: 0,
    maxZoom: 19,
  },

  initialize(type, options) {
    if (typeof type === 'object') {
      // eslint-disable-next-line no-param-reassign
      options = type
      // eslint-disable-next-line no-param-reassign
      type = false
    }
    const resolvedOptions = L.Util.setOptions(this, options)
    if (type) {
      resolvedOptions.type = type
    }
    this._isOverlay =
      resolvedOptions.type.indexOf('overlay') !== -1 ||
      resolvedOptions.type.indexOf('skeleton') !== -1
    this._animatedElements = []
  },

  _setStyle(el, style) {
    Object.keys(style).forEach((prop) => {
      // eslint-disable-next-line no-param-reassign
      el.style[prop] = style[prop]
    })
  },

  _initContainer(parentEl) {
    const zIndexClass = this._isOverlay
      ? 'leaflet-overlay-pane'
      : 'leaflet-tile-pane'
    const container = L.DomUtil.create(
      'div',
      `leaflet-yandex-container leaflet-pane ${zIndexClass}`
    )
    const opacity =
      this.options.opacity || (this._isOverlay && this.options.overlayOpacity)
    if (opacity) {
      L.DomUtil.setOpacity(container, opacity)
    }
    const auto = { width: '100%', height: '100%' }
    this._setStyle(parentEl, auto)
    this._setStyle(container, auto)
    return container
  },

  onAdd(map) {
    const mapPane = map.getPane('mapPane')
    if (!this._container) {
      this._container = this._initContainer(mapPane)
      map.once('unload', this._destroy, this)
      this._initApi()
    }
    mapPane.appendChild(this._container)
    if (!this._yandex) return
    this._setEvents(map)
    this._update()
  },

  beforeAdd(map) {
    map._addZoomLimit(this)
  },

  onRemove(map) {
    map._removeZoomLimit(this)
  },

  _destroy(e) {
    if (!this._map || this._map === e.target) {
      if (this._yandex) {
        this._yandex.destroy()
        delete this._yandex
      }
      delete this._container
    }
  },

  _setEvents(map) {
    const events = {
      move: this._update,
      resize: function resize() {
        this._yandex.container.fitToViewport()
      },
    }
    if (this._zoomAnimated) {
      events.zoomanim = this._animateZoom
      events.zoomend = this._animateZoomEnd
    }
    map.on(events, this)
    this.once(
      'remove',
      () => {
        map.off(events, this)
        this._container.remove()
      },
      this
    )
  },

  _update() {
    const map = this._map
    const center = map.getCenter()
    this._yandex.setCenter([center.lat, center.lng], map.getZoom())
    const offset = L.point(0, 0).subtract(
      L.DomUtil.getPosition(map.getPane('mapPane'))
    )
    L.DomUtil.setPosition(this._container, offset)
  },

  _animateZoom(e) {
    const map = this._map
    const viewHalf = map.getSize()._divideBy(2)
    const topLeft = map.project(e.center, e.zoom)._subtract(viewHalf)._round()
    const offset = map
      .project(map.getBounds().getNorthWest(), e.zoom)
      ._subtract(topLeft)
    const scale = map.getZoomScale(e.zoom)
    this._animatedElements.length = 0
    this._yandex.panes._array.forEach((el) => {
      if (el.pane instanceof ymaps.pane.MovablePane) {
        const element = el.pane.getElement()
        L.DomUtil.addClass(element, 'leaflet-zoom-animated')
        L.DomUtil.setTransform(element, offset, scale)
        this._animatedElements.push(element)
      }
    })
  },

  _animateZoomEnd() {
    this._animatedElements.forEach((el) => {
      L.DomUtil.setTransform(el, 0, 1)
    })
    this._animatedElements.length = 0
  },

  _initApi() {
    ymaps.ready(this._initMapObject, this)
  },

  _mapType() {
    const shortType = this.options.type
    if (!shortType || shortType.indexOf('#') !== -1) {
      return shortType
    }
    return `yandex#${shortType}`
  },

  _initMapObject() {
    ymaps.mapType.storage.add(
      'yandex#overlay',
      new ymaps.MapType('overlay', [])
    )
    ymaps.mapType.storage.add(
      'yandex#skeleton',
      new ymaps.MapType('skeleton', ['yandex#skeleton'])
    )
    ymaps.mapType.storage.add(
      'yandex#map~vector',
      new ymaps.MapType('map~vector', ['yandex#map~vector'])
    )
    const ymap = new ymaps.Map(
      this._container,
      {
        center: [0, 0],
        zoom: 0,
        behaviors: [],
        controls: [],
        type: this._mapType(),
      },
      this.options.mapOptions
    )

    if (this._isOverlay) {
      ymap.container.getElement().style.background = 'transparent'
    }
    this._container.remove()
    this._yandex = ymap
    if (this._map) this.onAdd(this._map)

    this.fire('load')
  },
})

L.yandex = (type, options) => new L.Yandex(type, options)

export default L
